import type { CSSProperties } from "react";
import { useEffect, useMemo, useState } from "react";

import Skeleton from "../../components/states/Skeleton";
import { listCustomers, type CustomerRow } from "../../lib/admin/customers";
import {
  listRuns,
  formatDuration,
  type RunSummary,
  type RunStageEntry,
  type RunFilters,
} from "../../lib/admin/runs";

/**
 * MER-489: /admin/runs. "Did last night's run work, and why not" for
 * any customer without SSH or SQL.
 *
 * Reads `pipeline_runs` directly (staff-readable as of migration
 * 0078). No proxy to the droplet's FastAPI `/runs/{id}/logs`: the run
 * orchestrator already writes per-stage status/error into
 * `pipeline_runs.stages`, so the failure detail this page needs is
 * already in Supabase.
 */

const STATUS_OPTIONS = ["running", "completed", "failed"] as const;

const STATUS_TONE: Record<string, string> = {
  completed: "var(--flag-green)",
  running: "var(--brand-teal)",
  failed: "var(--flag-red)",
  unknown: "var(--text-muted)",
};

function relativeTime(iso: string | null): string {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "Never";
  const delta = Date.now() - t;
  const hour = 60 * 60 * 1000;
  const day = 24 * hour;
  if (delta < 60 * 1000) return "just now";
  if (delta < hour) return `${Math.max(1, Math.round(delta / (60 * 1000)))}m ago`;
  if (delta < day) return `${Math.round(delta / hour)}h ago`;
  return `${Math.round(delta / day)}d ago`;
}

interface PageState {
  runs: RunSummary[];
  customers: CustomerRow[];
  isLoading: boolean;
  error: string | null;
}

export default function AdminRuns() {
  const [state, setState] = useState<PageState>({
    runs: [],
    customers: [],
    isLoading: true,
    error: null,
  });
  const [customerFilter, setCustomerFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [sinceFilter, setSinceFilter] = useState<string>("");
  const [untilFilter, setUntilFilter] = useState<string>("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [runs, customers] = await Promise.all([
          listRuns(
            {
              customerId: customerFilter || undefined,
              status: (statusFilter || undefined) as RunFilters["status"],
              since: sinceFilter || undefined,
              until: untilFilter || undefined,
            },
            200,
          ),
          listCustomers(),
        ]);
        if (cancelled) return;
        setState({ runs, customers, isLoading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          isLoading: false,
          error: err instanceof Error ? err.message : String(err),
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerFilter, statusFilter, sinceFilter, untilFilter]);

  const nameBySlug = useMemo(
    () => new Map(state.customers.map((c) => [c.slug, c.name])),
    [state.customers],
  );

  return (
    <main style={{ padding: "24px 32px", display: "grid", gap: 18 }}>
      <header style={headerStyle}>
        <div style={eyebrowStyle}>Admin</div>
        <h1 style={titleStyle}>Refresh runs</h1>
      </header>

      <section
        style={{ ...panelStyle, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}
      >
        <label style={filterLabelStyle}>
          Customer
          <select
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="">All customers</option>
            {state.customers.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label style={filterLabelStyle}>
          Status
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="">Any status</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label style={filterLabelStyle}>
          Since
          <input
            type="date"
            value={sinceFilter}
            onChange={(e) => setSinceFilter(e.target.value)}
            style={selectStyle}
          />
        </label>
        <label style={filterLabelStyle}>
          Until
          <input
            type="date"
            value={untilFilter}
            onChange={(e) => setUntilFilter(e.target.value)}
            style={selectStyle}
          />
        </label>
      </section>

      {state.isLoading ? <Skeleton label="Loading runs" height={240} /> : null}
      {state.error ? <ErrorCard message={state.error} /> : null}
      {!state.isLoading && !state.error ? (
        <section style={panelStyle}>
          {state.runs.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>
              No runs match these filters.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Customer</th>
                  <th style={headerCellStyle}>Started</th>
                  <th style={headerCellStyle}>Duration</th>
                  <th style={headerCellStyle}>Status</th>
                  <th style={headerCellStyle}>Trigger</th>
                  <th style={headerCellStyle} />
                </tr>
              </thead>
              <tbody>
                {state.runs.map((run) => {
                  const expanded = expandedId === run.id;
                  const displayStatus =
                    run.status === "completed" && run.hasFailedInsights ? "completed*" : run.status;
                  return (
                    <RunRow
                      key={run.id}
                      run={run}
                      displayName={nameBySlug.get(run.customerId) ?? run.customerId}
                      displayStatus={displayStatus}
                      expanded={expanded}
                      onToggle={() => setExpandedId(expanded ? null : run.id)}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      ) : null}
    </main>
  );
}

function RunRow(props: {
  run: RunSummary;
  displayName: string;
  displayStatus: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { run, displayName, displayStatus, expanded, onToggle } = props;
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: "pointer" }} aria-expanded={expanded}>
        <td style={cellStyle}>
          <span style={{ fontWeight: 600, color: "var(--text)" }}>{displayName}</span>
        </td>
        <td style={cellStyle}>{relativeTime(run.startedAt)}</td>
        <td style={cellStyle}>{formatDuration(run.durationSeconds)}</td>
        <td style={cellStyle}>
          <span
            style={{
              fontSize: 10,
              padding: "2px 8px",
              borderRadius: 8,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              color: STATUS_TONE[run.status] ?? STATUS_TONE.unknown,
              background: "rgba(0,0,0,0.04)",
            }}
          >
            {displayStatus}
          </span>
          {/* MER-489 acceptance: a failed insights stage must never
              hide inside an overall-completed run. */}
          {run.hasFailedInsights ? (
            <span
              style={{
                marginLeft: 6,
                fontSize: 10,
                padding: "2px 8px",
                borderRadius: 8,
                fontWeight: 700,
                background: "rgba(192,57,43,0.12)",
                color: "var(--flag-red)",
              }}
            >
              Insights failed
            </span>
          ) : null}
        </td>
        <td style={{ ...cellStyle, color: "var(--text-muted)" }}>
          {run.triggeredBy}
          {run.actor ? ` (${run.actor})` : ""}
        </td>
        <td style={{ ...cellStyle, color: "var(--text-muted)", fontSize: 11 }}>
          {expanded ? "▾ hide log" : "▸ show log"}
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={6} style={{ ...cellStyle, background: "rgba(0,0,0,0.02)" }}>
            <RunDetail run={run} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function RunDetail(props: { run: RunSummary }) {
  const { run } = props;
  return (
    <div style={{ display: "grid", gap: 10, padding: "6px 4px" }}>
      {run.error ? (
        <div style={{ fontSize: 12, color: "var(--flag-red)" }}>
          <strong>Run error:</strong> {run.error}
        </div>
      ) : null}
      {run.stages.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
          No per-stage detail recorded for this run.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={headerCellStyle}>Stage</th>
              <th style={headerCellStyle}>Status</th>
              <th style={{ ...headerCellStyle, textAlign: "right" }}>Rows in / out</th>
              <th style={headerCellStyle}>Error</th>
            </tr>
          </thead>
          <tbody>
            {run.stages.map((stage) => (
              <StageRow key={stage.key} stage={stage} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StageRow(props: { stage: RunStageEntry }) {
  const { stage } = props;
  return (
    <tr>
      <td style={cellStyle}>{stage.key}</td>
      <td style={{ ...cellStyle, color: STATUS_TONE[stage.status] ?? STATUS_TONE.unknown }}>
        {stage.status}
      </td>
      <td style={{ ...cellStyle, textAlign: "right" }}>
        {stage.rowsIn ?? "-"} / {stage.rowsOut ?? "-"}
      </td>
      <td style={{ ...cellStyle, color: "var(--flag-red)" }}>{stage.error ?? ""}</td>
    </tr>
  );
}

function ErrorCard(props: { message: string }) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 12,
        padding: 20,
        color: "var(--flag-red)",
        fontSize: 13,
      }}
    >
      {props.message}
    </div>
  );
}

const headerStyle: CSSProperties = {
  padding: "22px 28px",
  background: "linear-gradient(110deg, var(--brand-mint) 0%, #E6F3EE 50%, #FFFFFF 100%)",
  border: "1px solid var(--brand-line)",
  borderRadius: 8,
};

const eyebrowStyle: CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 1,
  color: "var(--text-2)",
  fontWeight: 700,
};

const titleStyle: CSSProperties = {
  margin: "6px 0 0 0",
  fontSize: 26,
  fontWeight: 800,
  letterSpacing: -0.5,
  color: "var(--text)",
};

const panelStyle: CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 12,
  padding: 18,
  display: "grid",
  gap: 12,
};

const filterLabelStyle: CSSProperties = {
  display: "grid",
  gap: 4,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  fontWeight: 700,
};

const selectStyle: CSSProperties = {
  font: "inherit",
  fontSize: 13,
  padding: "6px 8px",
  borderRadius: 6,
  border: "1px solid var(--border-subtle)",
  background: "var(--bg-card)",
  color: "var(--text)",
};

const cellStyle: CSSProperties = {
  padding: "10px 14px",
  fontSize: 13,
  borderBottom: "1px solid var(--border-subtle)",
  verticalAlign: "top",
  color: "var(--text)",
};

const headerCellStyle: CSSProperties = {
  textAlign: "left",
  padding: "8px 14px",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  borderBottom: "1px solid var(--border-subtle)",
  fontWeight: 700,
};
